import { useMemo, useRef } from 'react';

import { CirclePlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useTableFilters } from '@/components/data-table/filters-v2';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useMeasuredHeight } from '@/hooks/use-measured-height';
import { usePersistedTableFilters } from '@/hooks/use-persisted-table-filters';
import {
  CollapsibleHeaderSection,
  useCollapseSpacer,
} from '@/modules/common/components/organisms/collapsible-header-section';
import { PageTemplate } from '@/modules/common/components/templates/page-template';
import { usePermissions } from '@/modules/common/hooks/use-permissions';
import { OpsiumPolicy } from '@/modules/common/types/opsium-policy';
import { useUserProfile } from '@/modules/settings/hooks/account/use-user-profile';
import { AuthenticatedRoutes } from '@/router/routes';

import { useTimeOffBalancesRenditions } from '../components/balances/time-off-balances';
import { TimeOffsTable } from '../components/table/time-offs-table';
import { TimeOffsToolbar } from '../components/toolbar/time-offs-toolbar';
import {
  FILTER_KEY_DATE_RANGE,
  FILTER_KEY_REQUESTER_IDS,
  FILTER_KEY_TIME_CATEGORY,
  TIME_OFFS_TABLE_FILTER_KEYS,
  TIME_OFFS_TABLE_FILTERS_STORAGE_KEY,
} from '../constants/time-offs-filter-keys';
import { getTimeOffsFiltersConfig } from '../constants/time-offs-filters-config';
import { useTimeOffsDateRangeParam } from '../hooks/use-time-offs-date-range-param';
import { useTimeOffsTable } from '../hooks/use-time-offs-table';
import { TimeOffRequest } from '../types/time-off-request';
import { TimeOffTimeCategory } from '../types/time-off-time-category';

// Collapsing-toolbar heights (px). Hardcoded so the layout is stable from the
// first frame (no measurement flash). The title+filters height IS measured at
// runtime — see `useMeasuredHeight` below.
const BALANCES_HEADER_PX = 60; // title + subtitle block; cards start at this offset
const BALANCES_EXPANDED_PX = 240; // header (~60) + cards (~170, 3 rows) + slack (~10)
const BALANCES_COLLAPSED_PX = 52; // chip strip
const TITLE_FILTERS_FALLBACK_PX = 96; // used until the toolbar is measured

const getFilterValues = (value: string | string[] | undefined): string[] => {
  if (value === undefined) return [];
  return Array.isArray(value) ? value : [value];
};

export const TimeOffsPage = () => {
  const { t } = useTranslation('authenticated.timeOff');
  const navigate = useNavigate();
  const { hasAnyPermission, hasPermission } = usePermissions();
  const { userProfile } = useUserProfile();
  const canRequestTimeOff = hasAnyPermission([OpsiumPolicy.TimeOffsRequest]);
  const canViewOtherTimeOffs = hasPermission(OpsiumPolicy.TimeOffsViewOthers);

  const buildRowHref = (request: TimeOffRequest) =>
    AuthenticatedRoutes.TimeOffRequestDetails.replace(':requestId', String(request.id));

  const handleRowClick = (request: TimeOffRequest) => navigate(buildRowHref(request));

  const handleEditRequest = (request: TimeOffRequest) =>
    navigate(AuthenticatedRoutes.TimeOffRequestDetails.replace(':requestId', String(request.id)), {
      state: { startInEditMode: true },
    });

  usePersistedTableFilters({
    storageKey: TIME_OFFS_TABLE_FILTERS_STORAGE_KEY,
    filterKeys: TIME_OFFS_TABLE_FILTER_KEYS,
    defaultFilters: { [FILTER_KEY_TIME_CATEGORY]: TimeOffTimeCategory.Upcoming },
  });

  const filtersConfig = useMemo(
    () => getTimeOffsFiltersConfig({ includeRequesterFilter: canViewOtherTimeOffs }),
    [canViewOtherTimeOffs]
  );
  const filterState = useTableFilters(filtersConfig);
  const dateRange = useTimeOffsDateRangeParam();

  // The date-range lives outside the filters-v2 config, so it's absent from
  // `activeFilters`. Merge it back in so it reaches both the query key (triggers
  // refetch) and `mapTimeOffFilters` (applies the filter).
  const tableFilters = useMemo(
    () =>
      dateRange.serialized
        ? { ...filterState.activeFilters, [FILTER_KEY_DATE_RANGE]: dateRange.serialized }
        : filterState.activeFilters,
    [filterState.activeFilters, dateRange.serialized]
  );

  const { expandedHeader, expandedBody, collapsed } = useTimeOffBalancesRenditions();
  const shouldShowRequesterColumn =
    canViewOtherTimeOffs && getFilterValues(tableFilters[FILTER_KEY_REQUESTER_IDS]).length > 0;
  const { table, isLoading, hasMore, loadMore, errorMessage, saveColumnSizing } = useTimeOffsTable(
    tableFilters,
    t,
    {
      currentUserId: userProfile?.id,
      canViewOtherTimeOffs,
      shouldShowRequesterColumn,
      onEditRequest: handleEditRequest,
    }
  );

  // The single page scroll container drives the balances collapse.
  const scrollRef = useRef<HTMLDivElement>(null);

  // The table column header pins exactly below the (measured) toolbar.
  const { ref: toolbarRef, height: toolbarHeight } =
    useMeasuredHeight<HTMLDivElement>(TITLE_FILTERS_FALLBACK_PX);
  const tableHeaderTopPx = BALANCES_COLLAPSED_PX + toolbarHeight;

  const needsCollapseSpacer = useCollapseSpacer({
    scrollRef,
    collapseDistancePx: BALANCES_EXPANDED_PX - BALANCES_COLLAPSED_PX,
    contentDep: table.getRowModel().rows.length,
  });

  const handleRequestTimeOff = () => navigate(AuthenticatedRoutes.TimeOffRequestCreate);

  return (
    <PageTemplate
      title={t('title')}
      useStickyHeader
      className="p-0"
      headerClassName="items-start bg-surface px-4 pt-4 shadow-[0_1px_4px_0_hsl(var(--border))]"
      contentClassName="relative"
      rightActions={
        canRequestTimeOff ? (
          <Button size="sm" onClick={handleRequestTimeOff}>
            <CirclePlus className="h-4 w-4" />
            {t('header.requestTimeOff')}
          </Button>
        ) : undefined
      }
    >
      <Separator className="ml-[-16px] w-[calc(100%+32px)]" />

      {/* Single page scroll container. Sticky chrome stacks at the top:
          collapsing balances → toolbar → table column header. The table inside
          scrolls only horizontally. */}
      <div
        ref={scrollRef}
        className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain px-4 pb-4 pt-4"
        style={{ overflowAnchor: 'none' }}
      >
        {collapsed ? (
          <CollapsibleHeaderSection
            expandedHeader={expandedHeader}
            expandedBody={expandedBody}
            collapsed={collapsed}
            scrollContainerRef={scrollRef}
            expandedHeightPx={BALANCES_EXPANDED_PX}
            collapsedHeightPx={BALANCES_COLLAPSED_PX}
            bodyOffsetPx={BALANCES_HEADER_PX}
            className="z-30 bg-background"
          />
        ) : (
          // No collapse while loading/error/empty — render header + body statically.
          <div className="flex flex-col gap-3">
            {expandedHeader}
            {expandedBody}
          </div>
        )}

        <TimeOffsToolbar
          ref={toolbarRef}
          topPx={BALANCES_COLLAPSED_PX}
          filtersConfig={filtersConfig}
          filterState={filterState}
          dates={dateRange.dates}
          selectedPreset={dateRange.selectedPreset}
          onDateChange={dateRange.onChange}
          onPresetSelect={dateRange.onPresetSelect}
          onDateReset={dateRange.onReset}
          table={table}
        />

        <TimeOffsTable
          table={table}
          isLoading={isLoading}
          hasMore={hasMore}
          onLoadMore={loadMore}
          error={errorMessage}
          onResizeEnd={saveColumnSizing}
          hasActiveFilters={filterState.hasActiveFilters}
          onRequestTimeOff={canRequestTimeOff ? handleRequestTimeOff : undefined}
          onRowClick={handleRowClick}
          getRowHref={buildRowHref}
          stickyHeaderTopPx={tableHeaderTopPx}
        />

        {/* Guarantees enough scroll travel to fully collapse the balances even
            when the table is short. Suppressed when the content already
            overflows (otherwise it's dead space at the bottom). */}
        {needsCollapseSpacer && (
          <div
            aria-hidden
            className="shrink-0"
            style={{ height: BALANCES_EXPANDED_PX - BALANCES_COLLAPSED_PX }}
          />
        )}
      </div>
    </PageTemplate>
  );
};
